import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { CheckCircle, Circle } from 'lucide-react';

/**
 * OnboardingProgress — Beta dashboard widget showing portfolio insight progress.
 * Displays a segmented progress bar with contextual copy by stage.
 */
export function OnboardingProgress() {
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await api.get('/onboarding/progress');
        setProgress(res.data?.progress);
      } catch { /* no OVO record */ }
    };
    fetchProgress();
  }, []);

  if (!progress) return null;

  const score = progress.completion_score || 0;
  const state = progress.state;
  const isCompleted = progress.is_completed;

  const steps = [
    {
      label: 'First bid analysed',
      threshold: 20,
      done: score >= 20,
    },
    {
      label: '3 historical bids added',
      threshold: 60,
      done: score >= 60,
    },
    {
      label: '1 project outcome recorded',
      threshold: 100,
      done: score >= 100,
    },
  ];

  const stageText = isCompleted
    ? 'Validation complete'
    : state === 'signed_up'
      ? 'Analyse your first bid'
      : state === 'first_bid_completed' || state === 'historical_partial' || state === 'historical_invited'
        ? `Add ${Math.max(0, 3 - (progress.historical_bids_uploaded_count || 0))} more past bids`
        : state === 'outcome_pending' || state === 'validation_ready'
          ? 'Confirm one project outcome'
          : '';

  return (
    <Card className="card-industrial" data-testid="onboarding-progress-widget">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-800">Portfolio Insight Progress</h3>
          <span className="text-xs font-mono font-bold text-orange-600" data-testid="completion-score">
            {score}%
          </span>
        </div>

        {/* Progress Bar */}
        <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-4" data-testid="progress-bar">
          <div
            className="h-full rounded-full transition-all duration-700 ease-out"
            style={{
              width: `${score}%`,
              background: isCompleted
                ? '#10b981'
                : score >= 60
                  ? '#f97316'
                  : score >= 20
                    ? '#eab308'
                    : '#94a3b8',
            }}
          />
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {steps.map((step, i) => (
            <div key={i} className="flex items-center gap-2.5" data-testid={`progress-step-${i}`}>
              {step.done ? (
                <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
              ) : (
                <Circle className="w-4 h-4 text-slate-300 shrink-0" />
              )}
              <span className={`text-xs ${step.done ? 'text-slate-600 line-through' : 'text-slate-500'}`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {/* Stage text */}
        {stageText && (
          <p className="text-xs text-orange-600 font-medium mt-3 pt-3 border-t border-slate-100" data-testid="stage-text">
            {stageText}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
